import React, { useState, useEffect, useMemo } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  TextInput,
  FlatList,
  Platform,
  ScrollView,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import Modal from "react-native-modal";
import { Row } from "./all";
import { normalize } from "../utils/normalize";
import { COUNTRIES } from "../constants/countries";

const MultiCountrySelect = (props) => {
  const [showModal, setShowModal] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [selectedCountries, setSelectedCountries] = useState([]);

  // Use the countries from constants
  const countriesList = useMemo(() => {
    if (props.noUSD) {
      return Object.keys(COUNTRIES).map(code => ({
        code,
        name: COUNTRIES[code].name,
        flag: COUNTRIES[code].flag,
      })).filter(country => country.code !== 'USD');
    }
    return Object.keys(COUNTRIES).map(code => ({
      code,
      name: COUNTRIES[code].name,
      flag: COUNTRIES[code].flag,
    }));
  }, []);

  // Filter countries based on search text
  const filteredCountries = useMemo(() => {
    if (!searchText) return countriesList;

    const term = searchText.toLowerCase();
    return countriesList.filter(
      (country) =>
        country.name.toLowerCase().includes(term) ||
        country.code.toLowerCase().includes(term)
    );
  }, [searchText, countriesList]);

  useEffect(() => {
    // Set selected countries from props value (array of country codes)
    if (props.value && Array.isArray(props.value)) {
      const countries = props.value.map(code => {
        const countryData = COUNTRIES[code];
        return countryData ? {
          code,
          name: countryData.name,
          flag: countryData.flag,
        } : null;
      }).filter(Boolean);
      setSelectedCountries(countries);
    }
  }, [props.value]);

  const handleCountryToggle = (country) => {
    const isSelected = selectedCountries.some(c => c.code === country.code);
    let newSelection;

    if (isSelected) {
      // Remove country
      newSelection = selectedCountries.filter(c => c.code !== country.code);
    } else {
      // Add country
      newSelection = [...selectedCountries, country];
    }

    setSelectedCountries(newSelection);
    props.onChange && props.onChange(newSelection.map(c => c.code));
  };

  const handleRemoveCountry = (countryCode) => {
    const newSelection = selectedCountries.filter(c => c.code !== countryCode);
    setSelectedCountries(newSelection);
    props.onChange && props.onChange(newSelection.map(c => c.code));
  };

  const handleDone = () => {
    setShowModal(false);
    setSearchText("");
  };

  const getDisplayText = () => {
    if (selectedCountries.length === 0) {
      return props.placeholder || "Select Countries";
    }
    return `${selectedCountries.length} ${selectedCountries.length === 1 ? 'country' : 'countries'} selected`;
  };

  const renderCountryItem = ({ item }) => {
    const isSelected = selectedCountries.some(c => c.code === item.code);

    return (
      <TouchableOpacity
        onPress={() => handleCountryToggle(item)}
        style={{
          paddingVertical: 16,
          paddingHorizontal: 24,
          borderBottomWidth: 1,
          borderBottomColor: "#F0F0F0",
          backgroundColor: isSelected ? "#F0F8FF" : "white",
        }}
      >
        <Row>
          <Text style={{ fontSize: normalize(20), marginRight: 12 }}>
            {item.flag}
          </Text>
          <View style={{ flex: 1 }}>
            <Text
              style={{
                fontSize: normalize(16),
                color: "#333",
                fontWeight: isSelected ? "600" : "400",
              }}
            >
              {item.name}
            </Text>
            <Text
              style={{
                fontSize: normalize(12),
                color: "#666",
                marginTop: 2,
              }}
            >
              {item.code}
            </Text>
          </View>
          {isSelected && (
            <View
              style={{
                width: 24,
                height: 24,
                borderRadius: 12,
                backgroundColor: "#4B9BFF",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Ionicons name="checkmark" size={16} color="white" />
            </View>
          )}
        </Row>
      </TouchableOpacity>
    );
  };

  const renderModal = () => (
    <Modal
      isVisible={showModal}
      onBackdropPress={() => {
        setShowModal(false);
        setSearchText("");
      }}
      style={{ margin: 0, justifyContent: "flex-end", zIndex: 1000 }}
      animationIn="slideInUp"
      animationOut="slideOutDown"
      useNativeDriverForBackdrop={true}
    >
      <View
        style={{
          backgroundColor: "white",
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          height: Platform.OS === "ios" ? "90%" : "85%",
          paddingTop: 20,
        }}
      >
        {/* Header */}
        <View
          style={{
            borderBottomWidth: 1,
            borderBottomColor: "#E5E5E5",
            paddingBottom: 16,
            paddingHorizontal: 24,
          }}
        >
          <Row style={{ justifyContent: "space-between", marginBottom: 16 }}>
            <Text
              style={{
                fontSize: normalize(18),
                fontWeight: "600",
              }}
            >
              Select Countries
            </Text>
            <TouchableOpacity onPress={handleDone}>
              <Text
                style={{
                  fontSize: normalize(16),
                  color: "#4B9BFF",
                  fontWeight: "600",
                }}
              >
                Done
              </Text>
            </TouchableOpacity>
          </Row>

          {/* Search Bar */}
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              backgroundColor: "#F5F5F5",
              borderRadius: 8,
              paddingHorizontal: 12,
              paddingVertical: Platform.OS === "ios" ? 12 : 8,
            }}
          >
            <Ionicons name="search" size={20} color="#666" />
            <TextInput
              value={searchText}
              onChangeText={setSearchText}
              placeholder="Search country..."
              placeholderTextColor="#999"
              style={{
                flex: 1,
                marginLeft: 8,
                fontSize: normalize(14),
                color: "#333",
              }}
              autoCorrect={false}
              autoCapitalize="none"
              returnKeyType="search"
            />
            {searchText.length > 0 && (
              <TouchableOpacity onPress={() => setSearchText("")}>
                <Ionicons name="close-circle" size={20} color="#999" />
              </TouchableOpacity>
            )}
          </View>

          {/* Selected Count */}
          {selectedCountries.length > 0 && (
            <Text
              style={{
                marginTop: 8,
                fontSize: normalize(12),
                color: "#666",
              }}
            >
              {selectedCountries.length} selected
            </Text>
          )}
        </View>

        {/* Countries List */}
        <FlatList
          data={filteredCountries}
          keyExtractor={(item) => item.code}
          renderItem={renderCountryItem}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
          extraData={selectedCountries}
          ListEmptyComponent={
            <View
              style={{
                flex: 1,
                justifyContent: "center",
                alignItems: "center",
                paddingVertical: 48,
              }}
            >
              <Ionicons name="search" size={48} color="#CCC" />
              <Text
                style={{
                  marginTop: 16,
                  fontSize: normalize(16),
                  color: "#666",
                }}
              >
                No countries found
              </Text>
            </View>
          }
        />
      </View>
    </Modal>
  );

  return (
    <View style={props.style}>
      <TouchableOpacity
        onPress={() => setShowModal(true)}
        style={{
          flexDirection: "row",
          alignItems: "center",
          borderWidth: 1,
          borderColor: props.error ? "red" : "#E5E5E5",
          borderRadius: 8,
          paddingHorizontal: 16,
          paddingVertical: Platform.OS === "ios" ? 16 : 14,
          backgroundColor: "white",
          minHeight: 52,
        }}
      >
        <Ionicons name="globe-outline" size={16} color="grey" />
        <Text
          style={{
            flex: 1,
            marginLeft: 12,
            fontSize: normalize(14),
            color: selectedCountries.length > 0 ? "#333" : "#999",
          }}
        >
          {getDisplayText()}
        </Text>
        <Ionicons name="chevron-down" size={20} color="#666" />
      </TouchableOpacity>

      {/* Selected Countries Chips */}
      {selectedCountries.length > 0 && (
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={{
            marginTop: 8,
            marginBottom: 4,
          }}
        >
          <Row style={{ flexWrap: "nowrap" }}>
            {selectedCountries.map((country) => (
              <View
                key={country.code}
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  backgroundColor: "#F0F8FF",
                  borderRadius: 16,
                  paddingHorizontal: 12,
                  paddingVertical: 6,
                  marginRight: 8,
                  borderWidth: 1,
                  borderColor: "#4B9BFF",
                }}
              >
                <Text style={{ fontSize: normalize(16), marginRight: 4 }}>
                  {country.flag}
                </Text>
                <Text
                  style={{
                    fontSize: normalize(13),
                    color: "#333",
                    marginRight: 8,
                  }}
                >
                  {country.name}
                </Text>
                <TouchableOpacity
                  onPress={() => handleRemoveCountry(country.code)}
                  hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                >
                  <Ionicons name="close-circle" size={18} color="#4B9BFF" />
                </TouchableOpacity>
              </View>
            ))}
          </Row>
        </ScrollView>
      )}

      {props.error && selectedCountries.length === 0 && (
        <Text
          style={{
            marginTop: 4,
            marginLeft: 4,
            fontSize: normalize(12),
            color: "red",
          }}
        >
          Please select at least one country
        </Text>
      )}

      {renderModal()}
    </View>
  );
};

export default MultiCountrySelect;